import React, { useContext } from 'react';
import styled from '@emotion/styled';
import { scVarTransitionDuration } from '@utils/emotion/variables';
import { useTranslation } from 'react-i18next';
import { Upload as AntdUpload, Button, message } from 'antd';
import { RcFile } from 'antd/lib/upload';
import { UploaderContext } from '~/lib/video-uploader';
import { ReactComponent as Illust } from '@svg/illustration.svg';
import { ReactComponent as IconUpload } from '@svg/icon-upload-basic.svg';
import { ReactComponent as IconHelp } from '@svg/common/icon-help.svg';

const { Dragger } = AntdUpload;

const CardUpload: React.FC = () => {
  const { t, i18n } = useTranslation();
  const videoUploader = useContext(UploaderContext);

  const handleBeforeUpload = (file: RcFile) => {
    if (file.name.endsWith('.mkv') && file.type === '') return true;
    switch (file.type) {
      case 'video/mp4':
      case 'video/webm':
      case '.mkv':
      case 'video/quicktime':
      case 'video/x-msvideo':
      case 'video/x-flv':
        return true;
      default:
        message.error(t('upload.error.format'));
        return false;
    }
  };

  return (
    <CardBlock className="card-item">
      <div className="content">
        <strong className="title">{t('home.grant.heading')}</strong>
        <p className="desc">{t('home.grant.desc')}</p>
        <div className="dropzone-area">
          <div className="illustration" aria-hidden="true">
            <Illust width="216" height="240" />
          </div>
          <div className="dropzone">
            <Dragger
              name="video"
              multiple
              showUploadList={false}
              beforeUpload={handleBeforeUpload}
              customRequest={(options) => videoUploader.createVideoUpload(options)}
            >
              <div className="dropzone-inner">
                <Button type="primary" size="large" icon={<IconUpload />}>
                  {t('home.grant.upload')}
                </Button>
                <p className="drag-guide">{t('home.grant.guideline.drag')}</p>
              </div>
            </Dragger>
          </div>
        </div>
        <div className="link-area">
          <a
            href={
              i18n.languages[0] === 'ko'
                ? 'https://www.notion.so/81dc641e467e4e1bbd2a054d5985b3ec'
                : 'https://www.notion.so/How-to-upload-English-c691ec8280474acb96a7e4336475b69b'
            }
            target="_blank"
            rel="noopener noreferrer"
          >
            <p className="doc-link">
              <i className="icon">
                <IconHelp />
              </i>
              <span className="text">{t('home.grant.help.text')}</span>
            </p>
          </a>
        </div>
      </div>
    </CardBlock>
  );
};

const CardBlock = styled.div`
  position: relative;
  width: 350px;
  min-height: 408px;
  padding: 22px;
  background-color: ${({ theme }) => theme.colors.secondary.bgColor};
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.1);
  transition: background-color ${scVarTransitionDuration} ease;

  .content {
    .title {
      font-size: 18px;
      line-height: 1.5;
    }

    .desc {
      overflow: hidden;
      margin-top: 14px;
      font-size: 12px;
      line-height: 1.5;
      white-space: pre-wrap;
    }
  }

  .dropzone-area {
    position: relative;
    margin-top: 22px;

    .ant-upload-drag {
      padding: 0;
      border-color: ${({ theme }) => theme.colors.primary.btnColor};
      background: #faad14;
      transition: all 200ms ease;

      &.ant-upload-drag-hover {
        background-color: #ffcd69;
      }
    }

    .dropzone-inner {
      position: absolute;
      top: 38px;
      right: 20px;
      width: auto;
      height: auto;
      padding: 0;

      svg {
        position: relative;
        top: 2px;
        margin-right: 8px;
      }

      .ant-btn {
        width: 160px;
        font-size: 14px;
      }

      .drag-guide {
        margin: 8px;
        padding: 0 24px;
        color: ${({ theme }) => theme.colors.fontSubColor};
        font-size: 12px;
        line-height: 1.5;
        white-space: pre;
      }
    }
  }

  .illustration {
    position: absolute;
    top: 0;
    left: 19px;
    z-index: 1;
    pointer-events: none;
  }

  .button-area {
    position: absolute;
    top: 0;
    right: 0;
  }

  .dropzone {
    height: 265px;
  }

  .link-area {
    margin-top: 18px;

    a {
      display: inline-block;
      text-decoration: none;
      color: #5100ce;

      &:hover .text {
        text-decoration: underline;
      }
    }

    .doc-link {
      margin: 0;
      font-weight: bold;

      .icon {
        svg {
          vertical-align: top;
        }

        svg path {
          fill: #5100ce;
        }
      }

      .text {
        display: inline-block;
        margin-left: 8px;
        font-size: 15px;
        line-height: 1.2;
        vertical-align: 1px;
      }
    }
  }
`;

export default CardUpload;
